import {
  createContext,
  JSXElement,
  mergeProps,
  onCleanup,
  Show,
  useContext
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { useParams } from '@solidjs/router';
import {
  startListeningToEvents,
  stopListeningToEvents
} from '~/pages/Customizations/previewIframeUtils.ts';

export type PreviewModeContextValueType = {
  isPreviewModeEnabled: boolean;
  previewModeId: string;
};

const defaultContextValue: PreviewModeContextValueType = {
  isPreviewModeEnabled: false,
  previewModeId: String(new Date().getTime())
};

const PreviewModeContext =
  createContext<PreviewModeContextValueType>(defaultContextValue);

type PreviewModeContextProviderProps = {
  children: JSXElement;
};

export default function PreviewModeContextProvider(
  props: PreviewModeContextProviderProps
): JSXElement {
  const merged = mergeProps({}, props);
  const params = useParams();
  let overlayRef: HTMLDivElement | undefined;

  const [contextValue, setContextValue] =
    createStore<PreviewModeContextValueType>({
      ...defaultContextValue
    });

  const setPreviewMode = () => {
    const previewId = params.previewId;
    if (previewId) {
      setContextValue('isPreviewModeEnabled', true);
      setContextValue('previewModeId', previewId);
      startListeningToEvents();
    }
  };

  setPreviewMode();

  onCleanup(() => {
    stopListeningToEvents();
  });

  if (contextValue.isPreviewModeEnabled) {
    const intervalId: ReturnType<typeof setInterval> = setInterval(() => {
      const mainContainer = document.querySelector(
        '[data-main-container="true"]'
      );
      if (mainContainer && overlayRef) {
        clearInterval(intervalId);
        mainContainer.appendChild(overlayRef);
      }
    }, 100);
  }

  return (
    <PreviewModeContext.Provider value={contextValue}>
      <Show when={contextValue.isPreviewModeEnabled}>
        <div
          ref={overlayRef}
          class={`absolute top-0 right-0 bottom-0 left-0 z-[100] bg-transparent`}
        ></div>
      </Show>
      {merged.children}
    </PreviewModeContext.Provider>
  );
}

export const usePreviewModeContext = () =>
  useContext(PreviewModeContext) as PreviewModeContextValueType;
